import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Modal,
  message,
  Descriptions
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';
import '../../styles/third-party.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const auditStatusMap = {
  approved: { tone: 'paid', text: '已通过' },
  pending: { tone: 'processing', text: '未匹配' },
  skipped: { tone: 'muted', text: '已跳过' }
};

const orderTypeMap = {
  trade: { tone: 'pending', text: '收款单' },
  withdrawal: { tone: 'modified', text: '代付单' }
};

const EmailRecordList = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({});
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [searchForm] = Form.useForm();

  const loadData = async (params = {}) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/email-records', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          ...filters,
          ...params
        }
      });

      if (response.code === 200) {
        setDataSource(response.data.list);
        setPagination({
          ...pagination,
          total: response.data.pagination.total,
          current: response.data.pagination.page
        });
      }
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/admin/email-records/statistics', {
        params: filters
      });

      if (response.code === 200) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('加载统计数据失败', error);
    }
  };

  useEffect(() => {
    loadData();
    loadStatistics();
  }, []);

  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    delete newFilters.dateRange;

    setFilters(newFilters);
    setPagination({ ...pagination, current: 1 });
    loadData({ ...newFilters, page: 1 });
    loadStatistics();
  };

  const handleReset = () => {
    searchForm.resetFields();
    setFilters({});
    setPagination({ ...pagination, current: 1 });
    loadData({ page: 1 });
    loadStatistics();
  };

  const handleViewDetail = (record) => {
    setCurrentRecord(record);
    setDetailModalVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      render: (val) => val || <span className="tp-empty">-</span>
    },
    {
      title: '发件人',
      dataIndex: 'sender',
      width: 180,
      render: (sender) => sender ? <span className="tp-ellipsis" title={sender}>{sender}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '邮件主题',
      dataIndex: 'subject',
      width: 240,
      render: (subject) => subject ? <span className="tp-ellipsis" title={subject}>{subject}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '验证短码',
      dataIndex: 'short_code',
      width: 120,
      render: (code) => code || <span className="tp-empty">-</span>
    },
    {
      title: '金额',
      dataIndex: 'amount',
      width: 140,
      render: (amount, record) => amount
        ? (
          <div className="tp-amount">
            <span className="tp-amount__value">{Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span className="tp-amount__ccy">{record.currency_code || ''}</span>
          </div>
        )
        : <span className="tp-empty">-</span>
    },
    {
      title: '匹配订单',
      dataIndex: 'matched_trade_no',
      width: 180,
      render: (no) => no ? <span className="tp-ellipsis" title={no}>{no}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '类型',
      dataIndex: 'order_type',
      width: 90,
      render: (type) => {
        const config = orderTypeMap[type];
        return config
          ? <span className={`tp-tag tp-tag--${config.tone}`}>{config.text}</span>
          : <span className="tp-empty">-</span>;
      }
    },
    {
      title: '状态',
      dataIndex: 'audit_status',
      width: 100,
      render: (status) => {
        const config = auditStatusMap[status] || { tone: 'muted', text: status };
        return <span className={`tp-tag tp-tag--${config.tone}`}>{config.text}</span>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (time) => time ? <span className="tp-time">{dayjs(time).format('YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 88,
      fixed: 'right',
      render: (_, record) => (
        <div className="tp-actions">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="tp-page">
      {statistics && (
        <div className="tp-card tp-summary">
          <div className="tp-summary__item">
            <div className="tp-summary__label">总记录数</div>
            <div className="tp-summary__value tp-summary__value--accent">{Number(statistics.totalCount || 0).toLocaleString('en-US')}</div>
          </div>
          {(statistics.byStatus || []).map((stat) => {
            const tone = stat.status === 'approved' ? 'green' : '';
            return (
              <div className="tp-summary__item" key={stat.status}>
                <div className="tp-summary__label">{stat.statusName}</div>
                <div className={`tp-summary__value ${tone ? 'tp-summary__value--green' : ''}`}>{Number(stat.count || 0).toLocaleString('en-US')}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="tp-card tp-toolbar">
        <Form form={searchForm} layout="inline" className="tp-filters" onFinish={handleSearch}>
          <Form.Item name="shortCode">
            <Input placeholder="验证短码" className="tp-field--sm" allowClear />
          </Form.Item>
          <Form.Item name="sender">
            <Input placeholder="发件人" className="tp-field--md" allowClear />
          </Form.Item>
          <Form.Item name="matchedTradeNo">
            <Input placeholder="订单号" className="tp-field--md" allowClear />
          </Form.Item>
          <Form.Item name="auditStatus">
            <Select placeholder="状态" className="tp-field--sm" allowClear>
              <Option value="approved">已通过</Option>
              <Option value="pending">未匹配</Option>
              <Option value="skipped">已跳过</Option>
            </Select>
          </Form.Item>
          <Form.Item name="orderType">
            <Select placeholder="类型" className="tp-field--sm" allowClear>
              <Option value="trade">收款单</Option>
              <Option value="withdrawal">代付单</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker className="tp-field--date" />
          </Form.Item>
          <Form.Item name="keyword">
            <Input placeholder="搜索主题/内容/短码" className="tp-field--lg" allowClear />
          </Form.Item>
          <div className="tp-filters__actions">
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
            <Button onClick={handleReset} icon={<ReloadOutlined />}>重置</Button>
            <Button icon={<ReloadOutlined />} onClick={() => loadData()}>刷新</Button>
          </div>
        </Form>
      </div>

      <div className="tp-card tp-table-card">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          size="middle"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={(newPagination) => {
            setPagination(newPagination);
            loadData({ page: newPagination.current, limit: newPagination.pageSize });
          }}
          scroll={{ x: 1480 }}
        />
      </div>

      <Modal
        className="tp-modal"
        title="邮件记录详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {currentRecord && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="ID">{currentRecord.id}</Descriptions.Item>
            <Descriptions.Item label="消息ID">{currentRecord.message_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="发件人" span={2}>{currentRecord.sender || '-'}</Descriptions.Item>
            <Descriptions.Item label="邮件主题" span={2}>{currentRecord.subject || '-'}</Descriptions.Item>
            <Descriptions.Item label="验证短码">
              {currentRecord.short_code || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="金额">
              {currentRecord.amount
                ? `${Number(currentRecord.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currentRecord.currency_code || ''}`.trim()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="匹配订单" span={2}>
              {currentRecord.matched_trade_no || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="订单类型">
              {currentRecord.order_type ? (
                <span className={`tp-tag tp-tag--${orderTypeMap[currentRecord.order_type]?.tone || 'muted'}`}>
                  {orderTypeMap[currentRecord.order_type]?.text || currentRecord.order_type}
                </span>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <span className={`tp-tag tp-tag--${auditStatusMap[currentRecord.audit_status]?.tone || 'muted'}`}>
                {auditStatusMap[currentRecord.audit_status]?.text || currentRecord.audit_status}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="接收时间" span={2}>
              {currentRecord.received_at
                ? <span className="tp-time">{dayjs(currentRecord.received_at).format('YYYY-MM-DD HH:mm:ss')}</span>
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="邮件内容" span={2}>
              <div className="tp-pre">{currentRecord.content || '-'}</div>
            </Descriptions.Item>
            {currentRecord.audit_remark && (
              <Descriptions.Item label="备注" span={2}>
                <div className="tp-pre">{currentRecord.audit_remark}</div>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="创建时间">
              <span className="tp-time">{dayjs(currentRecord.created_at).format('YYYY-MM-DD HH:mm:ss')}</span>
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              <span className="tp-time">{dayjs(currentRecord.updated_at).format('YYYY-MM-DD HH:mm:ss')}</span>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default EmailRecordList;
